/** Resolver position patch → GeneratedLayout; untouched placements keep as-built geometry. */
import type { GeneratedLayout, GeneratedSegment, StudioItem } from "../domain/studioTypes";
import type { ResolverPosition } from "./cdtResolve";
import { placementId, type ResolverInputs } from "./toResolverInputs";

export const resolvedLayout = (
  items: readonly StudioItem[],
  positions: Readonly<Record<string, ResolverPosition>>,
  { hangLines, rankOfShelf }: Pick<ResolverInputs, "hangLines" | "rankOfShelf"> = {},
): GeneratedLayout => {
  const placements: Record<string, { skuId: string; segments: GeneratedSegment[] }> = {};
  for (const item of items) {
    // A layout holds shelves only: hanging products are immutable, so they never appear moved.
    const segments = item.placements.flatMap((placement, index) => {
      if (placement.pegboard) return [];
      const patch = positions[placementId(item.skuId, index)];
      const shelf = patch && (rankOfShelf ? rankOfShelf(patch.shelf) : patch.shelf);
      return patch && shelf !== undefined && !hangLines?.has(`${patch.run}|${shelf}`)
        ? [{ run: patch.run, shelf, xCm: patch.x, hFacings: patch.hf }]
        : [
            {
              run: placement.run,
              shelf: placement.shelf,
              xCm: placement.xCm,
              hFacings: placement.hFacings,
            },
          ];
    });
    if (segments.length > 0) placements[item.skuId] = { skuId: item.skuId, segments };
  }

  // A hanging SKU still holds its place in the walk, at the hole it hangs from.
  const anchorOf = (item: StudioItem) => {
    const segment = placements[item.skuId]?.segments[0];
    const placement = item.placements[0];
    return segment ?? { run: placement.run, shelf: placement.shelf, xCm: placement.xCm };
  };
  const walkOrder = [...items]
    .sort((a, b) => {
      const sa = anchorOf(a);
      const sb = anchorOf(b);
      return sa.run - sb.run || sa.shelf - sb.shelf || sa.xCm - sb.xCm;
    })
    .map((item) => item.skuId);

  return { placements, walkOrder };
};
